import { api } from './api';
import { WalletBalanceResponse, TransactionListResponse, Transaction, WalletCoupon, CouponValidationResult } from '../types/wallet';

export const walletService = {
  /**
   * Get wallet balance for a user
   * GET /user/:userId/wallet/balance
   */
  async getBalance(userId: string | number): Promise<WalletBalanceResponse> {
    const isB2B = window.location.pathname.startsWith('/b2b');
    const endpoint = isB2B ? `/b2b/wallet/balance/${userId}` : `/user/${userId}/wallet/balance`;
    const { data } = await api.get<WalletBalanceResponse>(endpoint);
    return data;
  },

  /**
   * Get wallet transactions for a user
   * GET /user/:userId/wallet/transactions?page=1&pageSize=10
   */
  async getTransactions(
    userId: string | number,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    transactionType?: string,
    startDate?: string,
    endDate?: string
  ): Promise<TransactionListResponse> {
    const isB2B = window.location.pathname.startsWith('/b2b');
    const endpoint = isB2B ? `/b2b/wallet/transactions/${userId}` : `/user/${userId}/wallet/transactions`;
    const { data } = await api.get<TransactionListResponse>(
      endpoint,
      {
        params: { page, pageSize, search, transactionType, startDate, endDate },
      }
    );
    return data;
  },

  /**
   * Get details for a single transaction
   * GET /user/:userId/wallet/view/:transactionId
   */
  async getTransactionDetail(
    userId: string | number,
    transactionId: string | number
  ): Promise<Transaction> {
    const isB2B = window.location.pathname.startsWith('/b2b');
    const endpoint = isB2B ? `/b2b/wallet/view/${userId}/${transactionId}` : `/user/${userId}/wallet/view/${transactionId}`;
    const { data } = await api.get<Transaction>(endpoint);
    return data;
  },

  /**
   * Get B2B wallet balance for a user
   * GET /b2b/wallet/balance/:userId
   */
  async getB2BBalance(userId: string | number): Promise<WalletBalanceResponse> {
    const { data } = await api.get<any>(`/b2b/wallet/balance/${userId}`);
    return {
      user_balance: data?.user_balance ?? data?.userBalance ?? 0,
      is_postpaid: data?.is_postpaid ?? data?.isPostpaid ?? 0,
      isPostpaid: data?.isPostpaid ?? data?.is_postpaid ?? 0,
      code: data?.code || 200,
      message: data?.message || '',
      success: data?.success ?? true,
      showPopUp: data?.showPopUp ?? false,
    };
  },

  /**
   * Get B2B wallet transactions for a user
   * GET /b2b/wallet/transactions/:userId?page=1&pageSize=10
   */
  async getB2BTransactions(
    userId: string | number,
    roleId?: string | number,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    transactionType?: string,
    startDate?: string,
    endDate?: string
  ): Promise<TransactionListResponse> {
    const { data } = await api.get<any>(
      `/b2b/wallet/transactions/${userId}`,
      {
        headers: roleId ? { roleId: roleId.toString() } : {},
        params: { page, pageSize, search, transactionType, startDate, endDate },
      }
    );
    return {
      total_count: data?.totalCount ?? data?.total_count ?? 0,
      transaction_list: data?.transactionList ?? data?.transaction_list ?? [],
      code: data?.code || 200,
      message: data?.message || '',
      success: data?.success ?? true,
      showPopUp: data?.showPopUp ?? false,
    };
  },

  /**
   * Get B2B details for a single transaction
   * GET /b2b/wallet/view/:userId/:transactionId
   */
  async getB2BTransactionDetail(
    userId: string | number,
    transactionId: string | number,
    roleId?: string | number
  ): Promise<Transaction> {
    const { data } = await api.get<any>(
      `/b2b/wallet/view/${userId}/${transactionId}`,
      {
        headers: roleId ? { roleId: roleId.toString() } : {},
      }
    );
    return data?.data ?? data;
  },

  async getAdminUserDetailsByCrn(crn: string): Promise<any> {
    const { data } = await api.get<any>(`/admin/wallet/user-details/${crn}`);
    return data;
  },

  async postAdminWalletTransaction(payload: {
    crn: string;
    payment_reference_number: string;
    amount: number;
    transaction_type: 'credit' | 'debit';
    remark: string;
    walletType: 'b2c' | 'b2b';
  }): Promise<any> {
    const { data } = await api.post<any>('/admin/wallet/transaction', payload);
    return data;
  },

  async getCoupons(userId?: string | number, type: 'B2C' | 'B2B' = 'B2C'): Promise<WalletCoupon[]> {
    const { data } = await api.get<any>('/wallet/coupons', {
      params: { user_id: userId, type },
    });
    return data?.data ?? [];
  },

  async validateCoupon(payload: {
    code: string;
    amount: number;
    user_id?: string | number;
    type?: 'B2C' | 'B2B';
  }): Promise<CouponValidationResult> {
    const { data } = await api.post<CouponValidationResult>('/wallet/coupons/validate', payload);
    return data;
  },
};
